import React from 'react';

interface DateOfBirthSelectProps {
  /** ISO date string "YYYY-MM-DD", or "" / partial while incomplete. */
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  idPrefix?: string;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Number of days in a given month/year, accounting for leap years. Used to
 * clamp the Day options so a 30/31-day selection can never be silently
 * rolled over into the next month by the Date constructor — see the bug
 * this component replaces, below.
 */
function daysInMonth(year: number, month1Indexed: number): number {
  // New Date(year, month, 0) is the last day of the PREVIOUS month when
  // month is 1-indexed as "the month after the one we want the length of".
  return new Date(year, month1Indexed, 0).getDate();
}

/**
 * Mobile-friendly Day/Month/Year date-of-birth entry (backlog #42), used in
 * place of a raw `<input type="date">` — native date pickers have
 * well-documented iOS/Android quirks with month/day wheel scrolling.
 *
 * Fixes a real bug found in the first (PersonalDetailsStep-only) version of
 * this pattern (backlog #54): the Day dropdown always listed 1-31
 * regardless of the selected month, so choosing day 31 (or 30/29 for
 * shorter months) while a 30-day-or-fewer month was selected produced an
 * invalid date string like "2010-06-31". `new Date(...)` silently rolls
 * that over to 1 July rather than throwing, so the member's date of birth
 * could end up a day (or, near a month/year boundary, more) different from
 * what they actually selected, with no visible error. This component
 * clamps the Day list to the selected month's real length and clears an
 * out-of-range Day if the month changes to a shorter one, so an invalid
 * combination can never be constructed in the first place.
 */
const DateOfBirthSelect: React.FC<DateOfBirthSelectProps> = ({
  value,
  onChange,
  error,
  idPrefix = 'dob',
}) => {
  const [y = '', m = '', d = ''] = (value || '').split('-');

  const nowYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => String(nowYear - i));

  const maxDay = y && m ? daysInMonth(Number(y), Number(m)) : 31;
  const days = Array.from({ length: maxDay }, (_, i) => String(i + 1).padStart(2, '0'));

  const update = (part: 'y' | 'm' | 'd', v: string) => {
    const nextY = part === 'y' ? v : y;
    const nextM = part === 'm' ? v : m;
    let nextD = part === 'd' ? v : d;

    // If the month (or year, for a 29 Feb -> non-leap-year change) changes
    // such that the currently-selected day no longer exists, clear the day
    // rather than silently keeping an out-of-range value that would only
    // become invalid once all three parts are set.
    if (nextY && nextM && nextD) {
      const limit = daysInMonth(Number(nextY), Number(nextM));
      if (Number(nextD) > limit) nextD = '';
    }

    if (nextY && nextM && nextD) {
      onChange(`${nextY}-${nextM.padStart(2, '0')}-${nextD.padStart(2, '0')}`);
    } else {
      onChange(`${nextY}-${nextM}-${nextD}`.replace(/-+$/, ''));
    }
  };

  const selectCls = `w-full px-2 py-2 border rounded-lg focus:ring-2 focus:ring-fishing-blue focus:border-transparent ${
    error ? 'border-red-500' : 'border-gray-300'
  }`;

  return (
    <div className="grid grid-cols-3 gap-2">
      <select
        id={`${idPrefix}-day`}
        aria-label="Day"
        value={d}
        onChange={(e) => update('d', e.target.value)}
        className={selectCls}
      >
        <option value="">Day</option>
        {days.map((x) => (
          <option key={x} value={x}>
            {Number(x)}
          </option>
        ))}
      </select>
      <select
        id={`${idPrefix}-month`}
        aria-label="Month"
        value={m}
        onChange={(e) => update('m', e.target.value)}
        className={selectCls}
      >
        <option value="">Month</option>
        {MONTHS.map((name, i) => (
          <option key={name} value={String(i + 1).padStart(2, '0')}>
            {name}
          </option>
        ))}
      </select>
      <select
        id={`${idPrefix}-year`}
        aria-label="Year"
        value={y}
        onChange={(e) => update('y', e.target.value)}
        className={selectCls}
      >
        <option value="">Year</option>
        {years.map((x) => (
          <option key={x} value={x}>
            {x}
          </option>
        ))}
      </select>
    </div>
  );
};

export default DateOfBirthSelect;
